import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, X, Loader2, Briefcase, Users, Package, CalendarClock, ArrowRight, Hash } from "lucide-react";

const GROUPS = [
  { key: "jobs", label: "Jobs", Icon: Briefcase, colour: "text-indigo-600 bg-indigo-50" },
  { key: "contacts", label: "Customers", Icon: Users, colour: "text-emerald-600 bg-emerald-50" },
  { key: "products", label: "Parts & Products", Icon: Package, colour: "text-amber-600 bg-amber-50" },
  { key: "serials", label: "Serial Numbers", Icon: Hash, colour: "text-purple-600 bg-purple-50" },
  { key: "shifts", label: "Shifts", Icon: CalendarClock, colour: "text-violet-600 bg-violet-50" },
];

export default function SearchOverlay({ open, onClose }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setQ(""); setData(null);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || q.trim().length < 2) { setData(null); return; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/search", { params: { q } })
        .then((r) => setData(r.data)).catch(() => setData(null))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  if (!open) return null;

  const go = (to) => { onClose(); navigate(to); };
  const groups = GROUPS.map((g) => ({ ...g, items: data?.[g.key] || [] })).filter((g) => g.items.length > 0);
  const hasQuery = q.trim().length >= 2;
  const empty = hasQuery && !loading && groups.length === 0;

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-sm flex flex-col" data-testid="search-overlay" onMouseDown={onClose}>
      <div className="w-full h-full bg-white flex flex-col" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-4 sm:px-8 py-4 border-b border-slate-100 shrink-0">
          <Search className="w-5 h-5 text-slate-400 shrink-0" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search jobs, customers, parts, serials, shifts…"
            className="flex-1 text-lg outline-none placeholder:text-slate-400"
            data-testid="search-overlay-input"
          />
          {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400" data-testid="search-overlay-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-4">
          {!hasQuery && <div className="px-4 py-20 text-center text-sm text-slate-400">Start typing to search across the app.</div>}
          {empty && <div className="px-4 py-20 text-center text-sm text-slate-400" data-testid="search-no-results">No matches for “{q}”.</div>}
          {groups.length > 0 && (
            <div className="columns-1 md:columns-2 lg:columns-3 gap-6 [column-fill:_balance]">
              {groups.map((g) => (
                <div key={g.key} className="mb-6 break-inside-avoid" data-testid={`search-group-${g.key}`}>
                  <div className="px-1 py-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
                    <span className={`w-5 h-5 rounded-md flex items-center justify-center ${g.colour}`}><g.Icon className="w-3.5 h-3.5" /></span>
                    {g.label}
                    <span className="ml-auto text-slate-300 font-semibold">{g.items.length}</span>
                  </div>
                  <div className="mt-1 rounded-xl border border-slate-100 overflow-hidden divide-y divide-slate-50">
                    {g.items.map((it) => (
                      <button
                        key={`${g.key}-${it.id}`}
                        onClick={() => go(it.to)}
                        className="group w-full text-left px-3 py-2.5 hover:bg-slate-50 flex items-center gap-3"
                        data-testid={`search-result-${g.key}-${it.id}`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-slate-800 truncate">{it.title}</div>
                          {it.sub && <div className="text-[11px] text-slate-400 truncate">{it.sub}</div>}
                        </div>
                        <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-indigo-500 group-hover:translate-x-0.5 transition-all shrink-0" />
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
